import { useCallback, useEffect, useState } from 'react';

import client from '../api/client';

// --- raw API wrappers ---------------------------------------------------
// Exposed as standalone functions so PrintableMasterGrid can save/load a
// single template by id without the list hook re-fetching the whole page.

export async function fetchPrintableTemplate(id) {
  // GET /printabletemplate/:id returns the full doc, elements included.
  const { data } = await client.get(`/printabletemplate/${id}`);
  return data;
}

export async function createPrintableTemplate(body) {
  // body: { name, grid: { rows, columns }, page: { widthIn, heightIn, marginsIn, cellPaddingIn, showBoundaries }, elements }
  const { data } = await client.post('/printabletemplate', body);
  return data;
}

export async function updatePrintableTemplate(id, body) {
  const { data } = await client.put(`/printabletemplate/${id}`, body);
  return data;
}

export async function deletePrintableTemplate(id) {
  // Soft delete.
  await client.delete(`/printabletemplate/${id}`);
}

// --- list hook for the template picker -----------------------------------
// GET /printabletemplate -> { items, page, limit, total, pages }, items omit
// `elements` to stay light. limit defaults high since the picker has no
// pagination UI of its own yet.

const EMPTY = { items: [], page: 1, limit: 100, total: 0, pages: 1 };

export default function usePrintableTemplates({ page = 1, limit = 100 } = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    const { data: res } = await client.get('/printabletemplate', { params: { page, limit } });
    setData(res && Array.isArray(res.items) ? res : EMPTY);
  }, [page, limit]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    client
      .get('/printabletemplate', { params: { page, limit } })
      .then(({ data: res }) => {
        if (active) setData(res && Array.isArray(res.items) ? res : EMPTY);
      })
      .catch((err) => {
        if (active) {
          setData(EMPTY);
          setError(err);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, limit]);

  return { ...data, loading, error, reload };
}
